import { useQuery } from "@tanstack/react-query";
import { INTERVAL } from "../../constants/clientTime";
import { useGraphToken } from "../useTokens";

const GRAPH_BATCH_SIZE = 20;

type GraphBatchResponseItem = {
  id: string;
  status: number;
  body?: { displayName?: string };
};

async function fetchDisplayNamesBatch(
  emails: string[],
  token: string
): Promise<Array<{ email: string; displayName: string }>> {
  if (!emails.length) return [];

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const requests = emails.map((email, index) => ({
    id: String(index),
    method: "GET",
    url: `/users/${encodeURIComponent(email)}?$select=displayName`,
  }));

  try {
    const response = await fetch("https://graph.microsoft.com/v1.0/$batch", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ requests }),
    });
    if (!response.ok) {
      return emails.map((email) => ({ email, displayName: "" }));
    }
    const data = (await response.json()) as {
      responses?: GraphBatchResponseItem[];
    };
    const byId = new Map<string, string>();
    (data.responses ?? []).forEach((r) => {
      const name = r.status === 200 ? (r.body?.displayName ?? "") : "";
      byId.set(r.id, name);
    });
    return emails.map((email, index) => ({
      email,
      displayName: byId.get(String(index)) ?? "",
    }));
  } catch {
    return emails.map((email) => ({ email, displayName: "" }));
  }
}

async function getDisplayNamesFromGraph(
  emails: string[],
  token: string
): Promise<Record<string, string>> {
  if (!emails.length) return {};

  const chunks: string[][] = [];
  for (let i = 0; i < emails.length; i += GRAPH_BATCH_SIZE) {
    chunks.push(emails.slice(i, i + GRAPH_BATCH_SIZE));
  }

  const chunkResults = await Promise.all(
    chunks.map((chunk) => fetchDisplayNamesBatch(chunk, token))
  );

  return chunkResults
    .flat()
    .reduce<Record<string, string>>((acc, { email, displayName }) => {
      acc[email] = displayName;
      return acc;
    }, {});
}

export function useGraphDisplayNames(emails: string[]) {
  const { data: token, isSuccess } = useGraphToken();
  const sortedEmails = [...new Set(emails)].sort();

  return useQuery({
    queryKey: ["graphDisplayNames", sortedEmails],
    queryFn: async () => getDisplayNamesFromGraph(sortedEmails, token!),
    enabled: isSuccess && sortedEmails.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 1 * INTERVAL.DAY,
  });
}
